import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../portfolio/PortfolioCommon'
import { Breadcrumbs } from '../components/ui/Breadcrumbs'
import { AssessmentQuestionCard, type AssessmentQuestionSaveState } from './AssessmentQuestion'
import { getAiUseEntryOptions } from '../register/register-api'
import { useTenant } from '../tenancy/TenantProvider'
import { ConfirmationAssigneePicker } from './ContextualPersonAssignment'
import {
  completeOwnerInput,
  getAssessmentWorkspace,
  saveAssessmentResponse,
  startAssessment,
  submitAssessmentWhenReady,
  type AssessmentConfirmation,
  type AssessmentQuestion,
} from './assessment-api'

const domainLabels: Record<string, string> = {
  people_consequences: 'People & consequences',
  information_privacy: 'Information & privacy',
  actions_authority: 'Actions & authority',
  reliability_correction: 'Reliability & correction',
  legal_professional_safety: 'Legal, professional & safety',
  product_operational_dependency: 'Product & operational dependency',
}

const confirmationLabels: Record<string, { title: string; authority: string }> = {
  product: { title: 'Product information confirmation', authority: 'Product owner or supplier representative' },
  technical: { title: 'Technical confirmation', authority: 'Technical owner able to confirm actual system behaviour' },
  security: { title: 'Security review', authority: 'Authorised security reviewer' },
  privacy: { title: 'Privacy review', authority: 'Authorised privacy reviewer' },
  legal_professional: { title: 'Legal or professional review', authority: 'Qualified legal, regulatory or professional reviewer' },
}

function titleCase(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function displayDate(value: string | null) {
  return value ? new Date(value).toLocaleDateString('en-AU', { dateStyle: 'medium' }) : 'Not recorded'
}

function sourceLabel(source: string) {
  if (source === 'register_inherited') return 'AI Register'
  if (source === 'product_inherited') return 'Product information'
  if (source === 'deterministic_derived') return 'Confirmed information already held'
  if (source === 'specialist_confirmed') return 'Specialist confirmation'
  return 'Formal Assessment'
}

function confirmationPresentation(confirmation: AssessmentConfirmation) {
  const base = confirmationLabels[confirmation.confirmationType] ?? {
    title: titleCase(confirmation.confirmationType) + ' review',
    authority: confirmation.requiredAuthority,
  }
  const specialty = confirmation.specialty ? ' · ' + titleCase(confirmation.specialty) : ''
  return { ...base, title: base.title + specialty }
}

function confirmationTone(status: string) {
  if (status === 'completed') return 'complete'
  if (status === 'reviewer_needs_assignment' || status === 'information_requested') return 'attention'
  if (status === 'awaiting_response') return 'current'
  return 'neutral'
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function conditionQuestionCodes(value: unknown): string[] {
  const codes = new Set<string>()
  const visit = (item: unknown) => {
    if (Array.isArray(item)) {
      item.forEach(visit)
      return
    }
    const row = object(item)
    const questionCode = typeof row.questionCode === 'string'
      ? row.questionCode
      : typeof row.question_code === 'string'
        ? row.question_code
        : ''
    if (questionCode) codes.add(questionCode)
    Object.values(row).forEach((nested) => {
      if (typeof nested === 'string' && /^R1-AS-/.test(nested)) codes.add(nested)
      visit(nested)
    })
  }
  visit(value)
  return [...codes]
}

function confirmationFacts(confirmation: AssessmentConfirmation, questions: AssessmentQuestion[]) {
  const codes = conditionQuestionCodes(confirmation.sourceRuleCondition)
  return codes.map((code) => questions.find((question) => question.questionCode === code))
    .filter((question): question is AssessmentQuestion => Boolean(question))
    .map((question) => ({
      label: question.prompt || question.title,
      value: question.answer?.answerDisplay || 'Needs confirmation',
    }))
}

function SafeAssessmentError() {
  return <section className="panel assessment-safe-error" role="alert">
    <h2>We couldn't load this assessment.</h2>
    <p>Your information has not been changed. Please try again.</p>
  </section>
}

export function AssessmentPage() {
  const { useCaseId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const { current } = useTenant()
  const organisationId = current?.organisation.id ?? ''
  const queryClient = useQueryClient()
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null)
  const [questionSaveState, setQuestionSaveState] = useState<AssessmentQuestionSaveState>('idle')
  const autoStartRef = useRef(false)
  const autoOwnerCompleteRef = useRef<string | null>(null)
  const autoSubmitRef = useRef<string | null>(null)

  const workspaceQuery = useQuery({ queryKey: ['r1-assessment-workspace', useCaseId], queryFn: () => getAssessmentWorkspace(useCaseId), enabled: Boolean(useCaseId) })
  const optionsQuery = useQuery({ queryKey: ['ai-use-entry-options', organisationId], queryFn: () => getAiUseEntryOptions(organisationId), enabled: Boolean(organisationId) })
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['r1-assessment-workspace', useCaseId] }),
      queryClient.invalidateQueries({ queryKey: ['phase3-governance-position', useCaseId] }),
      queryClient.invalidateQueries({ queryKey: ['ai-register'] }),
      queryClient.invalidateQueries({ queryKey: ['ai-risks'] }),
    ])
  }
  const startMutation = useMutation({ mutationFn: () => startAssessment(useCaseId), onSuccess: refresh })
  const saveMutation = useMutation({
    mutationFn: ({ question, value, code, display, notes }: { question: AssessmentQuestion; value: unknown; code: string | null; display: string | null; notes: string | null }) => {
      const assessmentId = workspaceQuery.data?.assessment?.id
      if (!assessmentId) throw new Error('Assessment is not available')
      return saveAssessmentResponse(assessmentId, question.questionVersionId, value, code, display, notes)
    },
    onSuccess: refresh,
  })
  const ownerCompleteMutation = useMutation({
    mutationFn: () => {
      const assessmentId = workspaceQuery.data?.assessment?.id
      if (!assessmentId) throw new Error('Assessment is not available')
      return completeOwnerInput(assessmentId)
    },
    onSuccess: refresh,
  })
  const submitMutation = useMutation({
    mutationFn: () => {
      const assessmentId = workspaceQuery.data?.assessment?.id
      if (!assessmentId) throw new Error('Assessment is not available')
      return submitAssessmentWhenReady(assessmentId)
    },
    onSuccess: refresh,
  })

  const workspace = workspaceQuery.data
  const knownQuestions = useMemo(
    () => (workspace?.questions ?? []).filter((question) => question.answer && question.answer.responseSource !== 'business_answer'),
    [workspace?.questions],
  )
  const businessQuestions = useMemo(
    () => (workspace?.questions ?? []).filter((question) => !question.answer || question.answer.responseSource === 'business_answer'),
    [workspace?.questions],
  )
  const unansweredQuestions = useMemo(
    () => businessQuestions.filter((question) => !question.answer),
    [businessQuestions],
  )
  const currentQuestionIndex = businessQuestions.findIndex((question) => question.questionVersionId === activeQuestionId)
  const currentQuestion = currentQuestionIndex >= 0 ? businessQuestions[currentQuestionIndex] : null
  const hasPendingQuestionSave = ['dirty', 'saving', 'error'].includes(questionSaveState)

  const assessment = workspace?.assessment
  const editable = Boolean(assessment && ['draft', 'in_progress'].includes(assessment.status) && !workspace?.readiness.ownerInputComplete)
  const error = workspaceQuery.error ?? optionsQuery.error ?? startMutation.error ?? ownerCompleteMutation.error ?? submitMutation.error

  useEffect(() => {
    if (businessQuestions.length === 0) {
      setActiveQuestionId(null)
      return
    }

    const activeStillVisible = businessQuestions.some((question) => question.questionVersionId === activeQuestionId)
    if (!activeStillVisible) {
      const nextQuestion = businessQuestions.find((question) => !question.answer) ?? businessQuestions[businessQuestions.length - 1]
      setActiveQuestionId(nextQuestion?.questionVersionId ?? null)
      setQuestionSaveState(nextQuestion?.answer ? 'saved' : 'idle')
    }
  }, [activeQuestionId, businessQuestions])

  useEffect(() => {
    if (!workspaceQuery.isLoading && workspace && !workspace.assessment && workspace.frameworkAvailable && !autoStartRef.current) {
      autoStartRef.current = true
      startMutation.mutate()
    }
  }, [startMutation, workspace, workspaceQuery.isLoading])

  useEffect(() => {
    if (!assessment || !editable || unansweredQuestions.length > 0 || ownerCompleteMutation.isPending || saveMutation.isPending) return
    if (autoOwnerCompleteRef.current === assessment.id) return
    autoOwnerCompleteRef.current = assessment.id
    ownerCompleteMutation.mutate()
  }, [assessment, editable, ownerCompleteMutation, saveMutation.isPending, unansweredQuestions.length])

  useEffect(() => {
    if (!assessment || !workspace?.readiness.canSubmit || submitMutation.isPending) return
    if (autoSubmitRef.current === assessment.id) return
    autoSubmitRef.current = assessment.id
    submitMutation.mutate()
  }, [assessment, submitMutation, workspace?.readiness.canSubmit])

  useEffect(() => {
    if (error) console.error('Formal Assessment operation failed', error)
  }, [error])

  if (!current) return <Navigate to="/workspace/create" replace />
  if (workspaceQuery.isLoading) return <div className="page-stack"><section className="panel"><p>Preparing assessment…</p></section></div>
  if (!workspace) return <div className="page-stack"><SafeAssessmentError /></div>

  const statusLabel = assessment ? titleCase(assessment.status) : 'Preparing'
  const knownFactCount = knownQuestions.length + 2
  const assessmentComplete = Boolean(workspace.readiness.ownerInputComplete)
  const specialistComplete = assessmentComplete && workspace.readiness.openConfirmations === 0
  const outcomeComplete = assessment?.status === 'finalised'
  const journey = [
    { label: 'Known facts', complete: true },
    { label: 'Assessment', complete: assessmentComplete },
    { label: 'Specialist review', complete: specialistComplete },
    { label: 'Risk outcome', complete: outcomeComplete },
  ]
  const currentJourneyIndex = Math.max(0, journey.findIndex((step) => !step.complete))

  return (
    <div className="page-stack assessment-page">
      <Breadcrumbs items={[{ label: 'AI Uses', to: '/ai-register' }, { label: workspace.aiUseCase.title, to: `/ai-register/${useCaseId}` }, { label: 'Assessment' }]} />
      <PageHeader
        eyebrow={'Formal assessment · ' + statusLabel}
        title={'Assess ' + workspace.aiUseCase.title}
        description="Swell reuses confirmed information and asks people only for facts or judgement it cannot determine itself."
      />
      {searchParams.get('from') === 'add' ? <section className="inline-alert success" aria-live="polite"><strong>AI use saved.</strong> Continue with the facts you know. Each answer is saved as you go, and you can finish later from the AI Use record.</section> : null}
      {error ? <SafeAssessmentError /> : null}

      {!assessment ? <section className="panel assessment-preparing" aria-live="polite">
        <h2>{workspace.frameworkAvailable ? 'Preparing your assessment…' : 'Assessment framework unavailable'}</h2>
        <p>{workspace.frameworkAvailable ? 'Swell is collecting the confirmed facts it already holds.' : 'Please try again later or contact your Swell administrator.'}</p>
      </section> : <>
        <nav className="assessment-journey" aria-label="Assessment journey">
          {journey.map((step, index) => <div key={step.label} className={'assessment-journey-step ' + (step.complete ? 'complete' : index === currentJourneyIndex ? 'current' : 'future')} aria-current={index === currentJourneyIndex ? 'step' : undefined}>
            <span aria-hidden="true">{step.complete ? '✓' : index + 1}</span>
            <strong>{step.label}</strong>
          </div>)}
        </nav>

        <section className="panel assessment-known" aria-labelledby="assessment-known-heading">
          <div className="section-heading"><div>
            <p className="eyebrow">Known facts reused: {knownFactCount}</p>
            <h2 id="assessment-known-heading">What Swell already knows</h2>
            <p><strong>Most of the information has already been collected.</strong> Swell will only ask about information it cannot determine from the AI Register, Product information or previous confirmed reviews.</p>
          </div><div className="assessment-question-count"><span>Questions remaining</span><strong>{unansweredQuestions.length}</strong></div></div>
          <div className="assessment-known-grid">
            <article><span aria-hidden="true">✓</span><div><strong>AI use</strong><small>{workspace.aiUseCase.title}</small></div></article>
            <article><span aria-hidden="true">✓</span><div><strong>Operating state</strong><small>{titleCase(workspace.aiUseCase.operationalState)}</small></div></article>
            {knownQuestions.slice(0, 6).map((question) => <article key={question.questionVersionId}><span aria-hidden="true">✓</span><div><strong>{question.title}</strong><small>{question.answer?.answerDisplay || 'Recorded'} · {sourceLabel(question.answer?.responseSource ?? '')}</small></div></article>)}
          </div>
          {knownQuestions.length > 6 ? <details><summary>View all reused facts</summary><ul>{knownQuestions.slice(6).map((question) => <li key={question.questionVersionId}><strong>{question.title}:</strong> {question.answer?.answerDisplay || 'Recorded'}</li>)}</ul></details> : null}
        </section>

        {!workspace.readiness.ownerInputComplete && currentQuestion ? (
          <section className="assessment-domain" aria-labelledby={'domain-' + currentQuestion.moduleCode}>
            <div className="assessment-question-context">
              <div>
                <p className="eyebrow">Assessment</p>
                <h2 id={'domain-' + currentQuestion.moduleCode}>{domainLabels[currentQuestion.moduleCode] ?? titleCase(currentQuestion.moduleCode)}</h2>
              </div>
              <p className="assessment-section-progress">Question {currentQuestionIndex + 1} of {businessQuestions.length}</p>
            </div>
            <AssessmentQuestionCard
              key={currentQuestion.questionVersionId}
              question={currentQuestion}
              disabled={!editable}
              onSave={(value, code, display, notes) => saveMutation.mutateAsync({ question: currentQuestion, value, code, display, notes })}
              onSaveStateChange={setQuestionSaveState}
            />
            <nav className="assessment-question-navigation" aria-label="Assessment question navigation">
              <button
                className="button secondary"
                type="button"
                disabled={currentQuestionIndex <= 0 || hasPendingQuestionSave}
                onClick={() => {
                  const previous = businessQuestions[currentQuestionIndex - 1]
                  if (previous) {
                    setActiveQuestionId(previous.questionVersionId)
                    setQuestionSaveState(previous.answer ? 'saved' : 'idle')
                  }
                }}
              >← Back</button>
              <button
                className="button primary"
                type="button"
                disabled={!currentQuestion.answer || hasPendingQuestionSave || currentQuestionIndex >= businessQuestions.length - 1}
                onClick={() => {
                  const next = businessQuestions[currentQuestionIndex + 1]
                  if (next) {
                    setActiveQuestionId(next.questionVersionId)
                    setQuestionSaveState(next.answer ? 'saved' : 'idle')
                  }
                }}
              >Continue →</button>
            </nav>
          </section>
        ) : null}

        {!workspace.readiness.ownerInputComplete && unansweredQuestions.length === 0 ? <section className="panel" aria-live="polite">
          <h2>Checking what happens next…</h2>
          <p>Swell has everything required from you and is determining whether specialist confirmation is needed.</p>
        </section> : null}

        {workspace.readiness.ownerInputComplete && workspace.confirmations.length > 0 ? <section className="panel assessment-confirmations" aria-labelledby="confirmation-heading">
          <div className="section-heading"><div><p className="eyebrow">Formal specialist review</p><h2 id="confirmation-heading">Specialist confirmations</h2><p>These are formal review requests created by the assessment. Each person confirms only the facts or professional judgement within their authority.</p></div></div>
          <div className="assessment-confirmation-grid">
            {workspace.confirmations.map((confirmation) => {
              const presentation = confirmationPresentation(confirmation)
              const assignedToCurrentUser = confirmation.assignedMembershipId === current.membershipId
              const facts = confirmationFacts(confirmation, workspace.questions)
              return <article className={'assessment-confirmation-card ' + confirmationTone(confirmation.status)} key={confirmation.id}>
                <div className="assessment-confirmation-heading"><div><h3>{presentation.title}</h3><p>{presentation.authority}</p></div><span>{assignedToCurrentUser && confirmation.status !== 'completed' ? 'Assigned to you' : titleCase(confirmation.status)}</span></div>
                <p>{confirmation.reason}</p>
                {confirmation.dueAt ? <p className="assessment-confirmation-due"><strong>Due:</strong> {displayDate(confirmation.dueAt)}</p> : null}
                <details><summary>Why is this formal review needed?</summary><p><strong>Reason:</strong> {confirmation.reason}</p>{facts.length > 0 ? <dl>{facts.map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}</dl> : <p>The formal assessment matched the saved condition using the recorded assessment information.</p>}<p><strong>Saved condition:</strong> The recorded assessment information met the versioned condition for this type of review.</p>{confirmation.sourceRuleExplanation ? <p><strong>Result:</strong> {confirmation.sourceRuleExplanation}</p> : <p><strong>Result:</strong> A formal {presentation.title.toLowerCase()} is required.</p>}</details>
                {confirmation.status === 'reviewer_needs_assignment' && optionsQuery.data ? <ConfirmationAssigneePicker
                  requirementId={confirmation.id}
                  members={optionsQuery.data.members}
                  onChanged={refresh}
                /> : null}
                {confirmation.status === 'completed' ? <Link className="button secondary" to={confirmation.deepLinkPath}>View confirmation</Link> : null}
                {confirmation.status !== 'completed' && assignedToCurrentUser ? <Link className="button primary" to={confirmation.deepLinkPath}>Review now</Link> : null}
                {confirmation.status !== 'completed' && confirmation.assignedMembershipId && !assignedToCurrentUser ? <span className="assessment-awaiting">Awaiting response</span> : null}
              </article>
            })}
          </div>
        </section> : null}

        {workspace.readiness.ownerInputComplete && workspace.readiness.openConfirmations > 0 ? <section className="panel assessment-finished" aria-live="polite">
          <p className="eyebrow">Your contribution is complete</p>
          <h2>You're finished for now</h2>
          <p>Swell is waiting for {workspace.confirmations.filter((item) => item.status !== 'completed').map((item) => confirmationPresentation(item).title).join(' and ')}. Once these responses are received, Swell will complete the deterministic risk assessment.</p>
          <strong>You do not need to do anything else unless a specialist asks for more information.</strong>
        </section> : null}

        {workspace.readiness.canSubmit || submitMutation.isPending ? <section className="panel" aria-live="polite">
          <h2>Completing the risk outcome…</h2>
          <p>Swell is completing the risk assessment.</p>
        </section> : null}

        {outcomeComplete ? <section className="panel assessment-outcome-complete">
          <p className="eyebrow">Risk outcome complete</p>
          <h2>Assessment complete</h2>
          <p>The assessment has been completed using your organisation’s current assessment rules.</p>
          <Link className="button primary" to={'/ai-register/' + useCaseId}>View outcome</Link>
        </section> : null}

        <details className="panel assessment-details">
          <summary>Assessment details</summary>
          <dl>
            <div><dt>Formal record</dt><dd>Assessment version {assessment.cycleNo}</dd></div>
            <div><dt>Started</dt><dd>{displayDate(assessment.startedAt)}</dd></div>
            <div><dt>Status</dt><dd>{statusLabel}</dd></div>
            <div><dt>Framework</dt><dd>Organisation assessment rules</dd></div>
            <div><dt>Framework version</dt><dd>Saved with this formal assessment</dd></div>
          </dl>
        </details>
      </>}
    </div>
  )
}
